import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, AlertCircle } from 'lucide-react';
import { getAllCompaniesApi } from '../../api/api';
import { showMessage } from '../../context/MessageContext.jsx';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import { usePagination } from '../../hooks/usePagination.js';
import './AllCompaniesList.css';

export default function AllCompaniesList() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 50;
  const { totalPages, goToPage, goToNextPage, goToPreviousPage } = usePagination(
    totalCount,
    limit,
    currentPage,
    setCurrentPage
  );

  useEffect(() => {
    fetchCompanies();
  }, [currentPage, search]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: limit,
        ...(search && { search }),
      };
      const res = await getAllCompaniesApi(params);
      setCompanies(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch (err) {
      showMessage('Failed to load companies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const getFeatureFlags = (company) => {
    const flags = [];
    if (company.billWise) flags.push('Bill-Wise');
    if (company.userWise) flags.push('User-Wise');
    if (company.beatWise) flags.push('Beat-Wise');
    if (company.approvalWise) flags.push('Approval-Wise');
    return flags.length > 0 ? flags.join(', ') : 'No features enabled';
  };

  const handleRowClick = (company) => {
    if (company.organizationId) {
      navigate(`/superadmin/organizations/${company.organizationId}/companies`);
    }
  };

  return (
    <div className="all-companies-list">
      <div className="all-companies-list-header">
        <div>
          <h1 className="all-companies-list-title">All Companies Overview</h1>
          <p className="all-companies-list-subtitle">
            View all companies across all organizations
          </p>
        </div>
      </div>

      <Card className="all-companies-list-card">
        {/* Filters */}
        <div className="all-companies-list-filters">
          <div className="all-companies-list-search">
            <Search size={20} className="all-companies-list-search-icon" />
            <Input
              type="text"
              placeholder="Search by name, ID, email, city, or state..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="all-companies-list-search-input"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="all-companies-list-loading">Loading...</div>
        ) : companies.length === 0 ? (
          <div className="all-companies-list-empty">
            <Building2 size={48} />
            <p>No companies found</p>
          </div>
        ) : (
          <>
            <div className="all-companies-list-table-container">
              <table className="all-companies-list-table">
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>ID</th>
                    <th>Contact</th>
                    <th>Location</th>
                    <th>Organization</th>
                    <th>Features</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => {
                    return (
                      <tr 
                        key={`${company.organizationTallyId}-${company.companyId}`}
                        onClick={() => handleRowClick(company)}
                        style={{ cursor: company.organizationId ? 'pointer' : 'default' }}
                      >
                        <td>
                          <strong>{company.companyName}</strong>
                        </td>
                        <td>{company.companyId}</td>
                        <td>
                          <div>
                            {company.companyEmail && (
                              <div>{company.companyEmail}</div>
                            )}
                            {company.companyContactNumber && (
                              <div className="all-companies-list-phone">{company.companyContactNumber}</div>
                            )}
                            {!company.companyEmail && !company.companyContactNumber && 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div>
                            {company.companyCity && (
                              <div>{company.companyCity}</div>
                            )}
                            {company.companyState && (
                              <div className="all-companies-list-state">{company.companyState}</div>
                            )}
                            {!company.companyCity && !company.companyState && 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div>
                            <div>{company.organizationName}</div>
                            <div className="all-companies-list-org-id">({company.organizationTallyId})</div>
                          </div>
                        </td>
                        <td>
                          <div className="all-companies-list-features">
                            {getFeatureFlags(company)}
                          </div>
                        </td>
                        <td>
                          <span className={`all-companies-list-badge ${company.status === 1 ? 'active' : 'inactive'}`}>
                            {company.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{formatDate(company.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                onNextPage={goToNextPage}
                onPreviousPage={goToPreviousPage}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}

